import { Drawer, FocusTrap, Box, Center } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import styles from './MobileMenu.module.scss';
import { ReactComponent as IconSortMobile } from '../img/filters/iconSortMobile.svg';
import { ReactComponent as IconSortMobileBlue } from '../img/filters/iconSortMobileBlue.svg';
import { ReactComponent as IconCloseBlue } from '../img/filters/iconCloseBlue.svg';
import { ReactComponent as IconFilterSettings } from '../img/filters/iconFilterSettingts.svg';
import { ReactComponent as IconFilterSettingsActive } from '../img/filters/iconFilterSettingtsActive.svg';
import { ReactComponent as IconCloseActive } from '../img/filters/iconCloseFilter.svg';
import { ReactComponent as IconChevronLeft } from '../img/iconChewronLeft.svg';
import classNames from 'classnames';
import { ContractsFilterCheckbox } from 'components/ContractsFilters/ContractsFilterCheckbox';
import { ContractsMobileFilterDrawer } from 'components/ContractsFiltersMobile/ContractsMobileFilterDrawer';
import { ContractsMobileFilterGroup } from 'components/ContractsFiltersMobile/ContractsMobileFilterGroup';
import { ContractsMobilePriceFilter } from 'components/ContractsFiltersMobile/ContractsMobilePriceFilter';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import { useState, useEffect, useCallback } from 'react';
import type { CategoryItem, ContractsFilterParams } from '../Filters/Filters';
import { ReactComponent as SearchIcon } from '../img/iconSearch2.svg';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';

interface FiltersType {
  id: number;
  keyword: string;
  label: string;
}
interface MobileTabsSortType {
  value: string;
  label: string;
  sort_direction: string;
}

function formatDateForAPI(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export const MobileMenu = ({
  mobileTabsSort,
  handleTabClickSort,
  activeSortName,
  activeSortDirection,
  dataFiltersMobile,
  initialFilters = {},
  onApplyFilters,
  onResetFilters,
  toggleSearchOpen,
}: {
  mobileTabsSort?: MobileTabsSortType[];
  handleTabClickSort: (valSort: string, valDirection: string) => void;
  activeSortName?: string;
  activeSortDirection?: string;
  localStorageKey?: string;
  setActiveSort_type?: (tab: string) => void;
  dataFiltersMobile?: {
    statuses: FiltersType[];
    mvd_notifications: FiltersType[];
    types?: FiltersType[];
    nationalities: FiltersType[];
  };
  initialFilters?: ContractsFilterParams;
  onApplyFilters?: (filters: ContractsFilterParams) => void;
  onResetFilters?: () => void;
  mobileTabs: MobileTabsSortType[];
  handleTabClick?: (val: string) => void;
  activeTabName?: string;
  toggleSearchOpen?: () => void;
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedFilter, { open: openFilter, close: closeFilter }] = useDisclosure(false);
  const [openedDate, { open: openDate, close: closeDate }] = useDisclosure(false);
  const [rangeValue, setRangeValue] = useState<[Date | null, Date | null]>([null, null]);
  const [categoriesKeywords, setCategoriesKeywords] = useState<CategoryItem[]>(
    initialFilters.categories ?? [],
  );
  const [nationalityKeywords, setNationalityKeywords] = useState<string[]>(
    initialFilters.nationalities ?? [],
  );
  const [typeKeywords, setTypeKeywords] = useState<string[]>(initialFilters.types ?? []);
  const [statusKeywords, setStatusKeywords] = useState<string[]>(
    initialFilters.statuses ?? [],
  );
  const [mvdKeywords, setMvdKeywords] = useState<string[]>(
    initialFilters.mvd_notifications ?? [],
  );
  const [limitFrom, setLimitFrom] = useState<number | undefined>(
    initialFilters.limit_from,
  );
  const [limitTo, setLimitTo] = useState<number | undefined>(initialFilters.limit_to);
  const [dateAddStart, setDateAddStart] = useState<Date | null>(
    initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
  );
  const [dateAddEnd, setDateAddEnd] = useState<Date | null>(
    initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
  );

  useEffect(() => {
    setNationalityKeywords(initialFilters.nationalities ?? []);
    setCategoriesKeywords(initialFilters.categories ?? []);
    setTypeKeywords(initialFilters.types ?? []);
    setStatusKeywords(initialFilters.statuses ?? []);
    setMvdKeywords(initialFilters.mvd_notifications ?? []);
    setLimitFrom(initialFilters.limit_from);
    setLimitTo(initialFilters.limit_to);
    setDateAddStart(
      initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
    );
    setDateAddEnd(
      initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
    );
  }, [
    initialFilters.nationalities,
    initialFilters.categories,
    initialFilters.statuses,
    initialFilters.types,
    initialFilters.mvd_notifications,
    initialFilters.limit_from,
    initialFilters.limit_to,
    initialFilters.date_add?.start,
    initialFilters.date_add?.end,
  ]);

  const [filterDrawerState, setFilterDrawerState] = useState<{
    opened: boolean;
    title: string;
    options: { id: string; label: string }[];
    selectedValues: string[];
    filterType: string;
  }>({
    opened: false,
    title: '',
    options: [],
    selectedValues: [],
    filterType: '',
  });

  const handleClearSort = () => {
    handleTabClickSort('default', 'asc');
  };

  const isSortTabActive = (tab: MobileTabsSortType) =>
    tab.value === activeSortName &&
    (tab.sort_direction ?? 'asc') === (activeSortDirection ?? 'asc');

  const [queryParamsCategorys, setQueryParamsCategorys] = useState({
    page: 1,
    search: '',
  });

  const handleCategorysServerSearch = useCallback((search: string) => {
    setQueryParamsCategorys((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: categorysFiltersData,
    isLoading: isLoadingCategorys,
    isFetching: isFetchingCategorys,
  } = useGetCategoryEmployedsQuery(
    {
      page: queryParamsCategorys.page,
      ...(queryParamsCategorys.search ? { search: queryParamsCategorys.search } : {}),
    },
    { skip: !openedFilter },
  );

  const hasNextCategoryPage =
    !!categorysFiltersData?.meta &&
    categorysFiltersData.meta.current_page < categorysFiltersData.meta.last_page;

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategorys) return;
    if (!hasNextCategoryPage) return;
    setQueryParamsCategorys((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingCategorys, hasNextCategoryPage]);

  const transformCategories = (): { id: string; label: string }[] => {
    return (categorysFiltersData?.data || []).map((c) => ({
      id: String(c.id),
      label: c.name || '',
    }));
  };

  const transformNationalities = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.nationalities || []).map((national) => ({
      id: national.keyword,
      label: national.label,
    }));
  };

  const transformType = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.types ?? []).map((item) => ({
      id: item.keyword,
      label: item.label,
    }));
  };

  const hasTypesFilter = (dataFiltersMobile?.types?.length ?? 0) > 0;

  const transformStatus = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.statuses || []).map((item) => ({
      id: item.keyword,
      label: item.label,
    }));
  };

  const transformMvd = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.mvd_notifications || []).map((item) => ({
      id: item.keyword,
      label: item.label,
    }));
  };

  const handleItemClick = (filterType: string, optionId: string) => {
    switch (filterType) {
      case 'nationalities': {
        if (nationalityKeywords.includes(optionId)) {
          setNationalityKeywords(nationalityKeywords.filter((k) => k !== optionId));
        } else {
          setNationalityKeywords([...nationalityKeywords, optionId]);
        }
        break;
      }
      case 'categories': {
        const isSelected = categoriesKeywords.some((item) => item.id === optionId);
        if (isSelected) {
          setCategoriesKeywords(categoriesKeywords.filter((k) => k.id !== optionId));
        } else {
          const newCategory = {
            id: optionId,
            name:
              transformCategories().find((opt) => opt.id === optionId)?.label || optionId,
          };
          setCategoriesKeywords([...categoriesKeywords, newCategory]);
        }
        break;
      }
      case 'types': {
        if (typeKeywords.includes(optionId)) {
          setTypeKeywords(typeKeywords.filter((k) => k !== optionId));
        } else {
          setTypeKeywords([...typeKeywords, optionId]);
        }
        break;
      }
      case 'mvd_notifications': {
        if (mvdKeywords.includes(optionId)) {
          setMvdKeywords(mvdKeywords.filter((k) => k !== optionId));
        } else {
          setMvdKeywords([...mvdKeywords, optionId]);
        }
        break;
      }
      case 'statuses': {
        if (statusKeywords.includes(optionId)) {
          setStatusKeywords(statusKeywords.filter((k) => k !== optionId));
        } else {
          setStatusKeywords([...statusKeywords, optionId]);
        }
        break;
      }
    }
  };

  const handleItemRemove = (filterType: string, optionId: string) => {
    handleItemClick(filterType, optionId);
  };

  const openFilterDrawer = (filterType: string) => {
    let options: { id: string; label: string }[] = [];
    let selectedValues: string[] = [];
    let title = '';

    switch (filterType) {
      case 'nationalities':
        options = transformNationalities();
        selectedValues = nationalityKeywords;
        title = 'Гражданство';
        break;
      case 'categories':
        options = transformCategories();
        selectedValues = categoriesKeywords.map((c) => c.id);
        title = 'Категории';
        break;
      case 'types':
        options = transformType();
        selectedValues = typeKeywords;
        title = 'Типы';
        break;
      case 'mvd_notifications':
        options = transformMvd();
        selectedValues = mvdKeywords;
        title = 'МВД';
        break;
      case 'statuses':
        options = transformStatus();
        selectedValues = statusKeywords;
        title = 'Статус';
        break;
    }

    setFilterDrawerState({
      opened: true,
      title,
      options,
      selectedValues,
      filterType,
    });
  };

  const handleFilterDrawerChange = (values: string[]) => {
    switch (filterDrawerState.filterType) {
      case 'nationalities':
        setNationalityKeywords(values);
        break;
      case 'categories': {
        const categoryItems = values.map((id) => ({
          id,
          name:
            transformCategories().find((opt) => opt.id === id)?.label ||
            categoriesKeywords.find((c) => c.id === id)?.name ||
            id,
        }));
        setCategoriesKeywords(categoryItems);
        break;
      }
      case 'mvd_notifications':
        setMvdKeywords(values);
        break;
      case 'statuses':
        setStatusKeywords(values);
        break;
      case 'types':
        setTypeKeywords(values);
        break;
    }
    setFilterDrawerState((prev) => ({ ...prev, selectedValues: values }));
  };

  const handleFilterDrawerApply = () => {
    setFilterDrawerState((prev) => ({ ...prev, opened: false }));
  };

  const handleFilterDrawerReset = () => {
    switch (filterDrawerState.filterType) {
      case 'nationalities':
        setNationalityKeywords([]);
        break;
      case 'categories':
        setCategoriesKeywords([]);
        break;
      case 'mvd_notifications':
        setMvdKeywords([]);
        break;
      case 'statuses':
        setStatusKeywords([]);
        break;
      case 'types':
        setTypeKeywords([]);
        break;
    }
    setFilterDrawerState((prev) => ({
      ...prev,
      opened: false,
      selectedValues: [],
    }));
  };

  const openDatePicker = () => {
    setRangeValue([dateAddStart, dateAddEnd]);
    openDate();
  };

  const getAppliedFiltersCount = (): number => {
    let count = 0;
    if ((initialFilters.nationalities?.length ?? 0) > 0) count++;
    if ((initialFilters.categories?.length ?? 0) > 0) count++;
    if ((initialFilters.types?.length ?? 0) > 0) count++;
    if ((initialFilters.mvd_notifications?.length ?? 0) > 0) count++;
    if ((initialFilters.statuses?.length ?? 0) > 0) count++;
    if (initialFilters.limit_from != null || initialFilters.limit_to != null) count++;
    if (initialFilters.date_add?.start != null || initialFilters.date_add?.end != null) {
      count++;
    }
    return count;
  };

  const activeFiltersCount = getAppliedFiltersCount();

  const buildFiltersPayload = (): ContractsFilterParams => {
    const start = dateAddStart;
    const end = dateAddEnd ?? dateAddStart;
    const date_add =
      start != null && end != null
        ? { start: formatDateForAPI(start), end: formatDateForAPI(end) }
        : undefined;
    return {
      statuses: statusKeywords,
      types: typeKeywords,
      mvd_notifications: mvdKeywords,
      nationalities: nationalityKeywords,
      categories: categoriesKeywords,
      limit_from: limitFrom,
      limit_to: limitTo,
      date_add,
    };
  };

  const handleApplyFilters = () => {
    onApplyFilters?.(buildFiltersPayload());
    closeFilter();
  };

  const handleResetAllFilters = () => {
    setNationalityKeywords([]);
    setCategoriesKeywords([]);
    setTypeKeywords([]);
    setStatusKeywords([]);
    setMvdKeywords([]);
    setLimitFrom(undefined);
    setLimitTo(undefined);
    setDateAddStart(null);
    setDateAddEnd(null);
    onResetFilters?.();
    closeFilter();
  };

  const formatDateRangeForDisplay = (start: Date | null, end: Date | null): string => {
    if (!start && !end) return '';
    if (start && !end) return formatDateForDisplay(start);
    if (!start && end) return formatDateForDisplay(end);
    if (start && end && start.getTime() === end.getTime()) {
      return formatDateForDisplay(start);
    }
    return start && end
      ? `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`
      : '';
  };

  return (
    <>
      <div className={styles.root}>
        <div className={styles.search} onClick={toggleSearchOpen}>
          <SearchIcon />
        </div>

        <div
          className={styles.selector}
          onClick={open}
          style={{ background: activeSortName === 'default' ? '#FFFFFF' : '#E1E6FE' }}>
          {activeSortName === 'default' ? <IconSortMobile /> : <IconSortMobileBlue />}
          {activeSortName !== 'default' && (
            <IconCloseBlue
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                handleClearSort();
              }}
            />
          )}
        </div>

        {activeFiltersCount === 0 && (
          <div className={styles.filter} onClick={openFilter}>
            <IconFilterSettings />
            <span>Фильтр</span>
          </div>
        )}
        {activeFiltersCount === 1 && (
          <div
            className={classNames(styles.filter, styles.filterActive)}
            onClick={openFilter}>
            <IconFilterSettingsActive />
            <span>Фильтр</span>
            <IconCloseActive
              style={{ marginLeft: '4px' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleResetAllFilters();
              }}
            />
          </div>
        )}
        {activeFiltersCount >= 2 && (
          <div
            className={classNames(styles.filter, styles.filterActive)}
            onClick={openFilter}>
            <IconFilterSettingsActive />
            <span>Фильтр</span>
            <span className={styles.filterCount}>{activeFiltersCount}</span>
            <IconCloseActive
              style={{ marginLeft: '4px' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleResetAllFilters();
              }}
            />
          </div>
        )}
      </div>

      <Drawer
        opened={opened}
        onClose={close}
        position='bottom'
        size={600}
        zIndex={1000}
        withCloseButton={false}
        styles={{
          content: {
            borderRadius: '13px 13px 0px 0px',
            backgroundColor: '#FFF',
            padding: '12px 16px 32px 16px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}>
        {mobileTabsSort &&
          mobileTabsSort.map((tab, index) => {
            const direction = tab.sort_direction ?? 'asc';
            return (
              <div
                key={`${tab.value}_${direction}`}
                className={`${styles.tab} ${
                  isSortTabActive(tab) ? styles.tab_active : ''
                } ${index === 0 ? styles.tab_first : ''} ${
                  index === mobileTabsSort.length - 1 ? styles.tab_last : ''
                }`}
                onClick={() => {
                  handleTabClickSort(tab.value, direction);
                  close();
                }}>
                {tab.label}
              </div>
            );
          })}
      </Drawer>

      <Drawer
        opened={openedFilter}
        onClose={closeFilter}
        styles={{
          content: {
            backgroundColor: '#FFF',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={'100%'}
        zIndex={1000}
        withCloseButton={false}>
        <FocusTrap.InitialFocus />
        <div>
          <div className={styles.header}>
            <div className={styles.filterIcon}>
              <IconChevronLeft onClick={closeFilter} />
            </div>
            <span className={styles.filterTitle}>Фильтр</span>
            <span
              className={styles.filterClear}
              onClick={() => {
                handleResetAllFilters();
                closeFilter();
              }}>
              Сбросить
            </span>
          </div>

          <div className={styles.filterGroups}>
            {hasTypesFilter && (
              <ContractsMobileFilterGroup
                label='Тип договора'
                options={transformType()}
                selectedValues={typeKeywords}
                onItemClick={(optionId) => handleItemClick('types', optionId)}
                onItemRemove={(optionId) => handleItemRemove('types', optionId)}
                onShowAll={() => openFilterDrawer('types')}
                count={3}
              />
            )}

            <ContractsMobileFilterGroup
              label='Категории'
              options={transformCategories()}
              selectedValues={categoriesKeywords}
              onItemClick={(optionId) => handleItemClick('categories', optionId)}
              onItemRemove={(optionId) => handleItemRemove('categories', optionId)}
              onShowAll={() => openFilterDrawer('categories')}
              count={4}
            />

            <ContractsMobileFilterGroup
              label='Статус'
              options={transformStatus()}
              selectedValues={statusKeywords}
              onItemClick={(optionId) => handleItemClick('statuses', optionId)}
              onItemRemove={(optionId) => handleItemRemove('statuses', optionId)}
              onShowAll={() => openFilterDrawer('statuses')}
              count={3}
            />

            <div>
              <div className={styles.label}>
                <span>Лимит</span>
              </div>
              <ContractsMobilePriceFilter
                fromValue={limitFrom}
                toValue={limitTo}
                onChange={(from, to) => {
                  setLimitFrom(from);
                  setLimitTo(to);
                }}
              />
            </div>

            <ContractsMobileFilterGroup
              label='Гражданство'
              options={transformNationalities()}
              selectedValues={nationalityKeywords}
              onItemClick={(optionId) => handleItemClick('nationalities', optionId)}
              onItemRemove={(optionId) => handleItemRemove('nationalities', optionId)}
              onShowAll={() => openFilterDrawer('nationalities')}
              count={4}
            />

            <div>
              <div className={styles.label}>
                <span>МВД</span>
              </div>
              <div
                className={styles.genderCheckboxRow}
                style={{ flexDirection: 'column', alignItems: 'start' }}>
                {transformMvd().map((option) => (
                  <div key={option.id} className={styles.genderCheckboxOption}>
                    <ContractsFilterCheckbox
                      checked={mvdKeywords.includes(option.id)}
                      onChange={() => handleItemClick('mvd_notifications', option.id)}
                      label={option.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <FilterButton variant='apply' onClick={handleApplyFilters}>
            Применить
          </FilterButton>
        </div>
      </Drawer>

      <ContractsMobileFilterDrawer
        opened={filterDrawerState.opened}
        onClose={() => setFilterDrawerState((prev) => ({ ...prev, opened: false }))}
        title={filterDrawerState.title}
        options={
          filterDrawerState.filterType === 'categories'
            ? transformCategories()
            : filterDrawerState.options
        }
        selectedValues={filterDrawerState.selectedValues}
        onChange={handleFilterDrawerChange}
        onApply={handleFilterDrawerApply}
        onReset={handleFilterDrawerReset}
        showSearch={
          filterDrawerState.filterType === 'categories'
            ? true
            : filterDrawerState.options.length > 6
        }
        categoriesServer={filterDrawerState.filterType === 'categories'}
        onCategoriesSearch={
          filterDrawerState.filterType === 'categories'
            ? handleCategorysServerSearch
            : undefined
        }
        categoriesHasNext={
          filterDrawerState.filterType === 'categories' && hasNextCategoryPage
        }
        onCategoriesLoadMore={
          filterDrawerState.filterType === 'categories' ? loadMoreCategories : undefined
        }
        categoriesFetching={
          filterDrawerState.filterType === 'categories' && isFetchingCategorys
        }
        categoriesLoading={
          filterDrawerState.filterType === 'categories' && isLoadingCategorys
        }
      />

      <Drawer
        opened={openedDate}
        onClose={closeDate}
        styles={{
          content: {
            borderRadius: '13px 13px 0px 0px',
            backgroundColor: '#FFF',
            padding: '12px 16px 32px 16px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={520}
        zIndex={1000}
        withCloseButton={false}>
        <Box>
          <div className={styles.datePickerHeader}>
            <span className={styles.datePickerTitle}>Дата добавления</span>
            <span
              className={styles.datePickerReset}
              onClick={() => {
                setRangeValue([null, null]);
                setDateAddStart(null);
                setDateAddEnd(null);
                closeDate();
              }}>
              Сбросить
            </span>
          </div>
          <Center>
            <DatePicker
              size='lg'
              type='range'
              value={rangeValue}
              onChange={(value) => {
                const newValue = value as [Date | null, Date | null];
                setRangeValue(newValue);
                if (newValue[0] != null) setDateAddStart(newValue[0]);
                if (newValue[1] != null) setDateAddEnd(newValue[1]);
                if (newValue[0] != null && newValue[1] != null) {
                  closeDate();
                }
              }}
              highlightToday
              allowSingleDateInRange
            />
          </Center>
        </Box>
      </Drawer>
    </>
  );
};
